import { ENDPOINTS, ENDPOINT_METHODS, QUERYKEYS, createEndpointUrl } from './ChatApiConstants';
import {
    QUERYKEYS as CONTENT_QUERYKEYS,
    ENDPOINTS as CONTENT_ENDPOINTS,
    createEndpointUrl as createContentEndpointUrl,
} from './ContentApiConstants';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { localFetch } from './LocalChatFetch';
import { createFetchWrapper } from './WebUtils';
// @TEMPLATE_@MSAL - Uncomment this line to use MSAL for authentication.
import { useMsal } from '@azure/msal-react';

const DEBUG = true;

const fetchWrapper = createFetchWrapper({
    createEndpointUrl,
    localFetch,
    endpointMethods: ENDPOINT_METHODS
});

export function useGetChatSessions({ parameters, enabled }) {
    // @TEMPLATE_@MSAL - Uncomment this line to use MSAL for authentication.
    const msal = useMsal();

    return useQuery({
        queryKey: [
            // QueryKey goes here. See ApiConstants.js for more info.
            QUERYKEYS.getChatSessions,
            // Parameters go here.
            parameters
        ],
        // Query function to make the actual query with.
        queryFn: async ({ queryKey }) => {
            if (DEBUG) console.log("Api :: getChatSessions");

            const [_key, { parameters }] = queryKey;

            const result = await fetchWrapper({
                endpoint: ENDPOINTS.getChatSessions,
                parameters,
                msal
            });

            return { queryKey: _key, result };
        },
        // To disable the query, use this.
        enabled,
        // Make sure this option makes sense to your context.
        refetchOnWindowFocus: false,
    });
}

export function useGetChatSession({ parameters, enabled }) {
    // @TEMPLATE_@MSAL - Uncomment this line to use MSAL for authentication.
    const msal = useMsal();

    return useQuery({
        queryKey: [
            // QueryKey goes here. See ApiConstants.js for more info.
            QUERYKEYS.getChatSession,
            // Parameters go here.
            parameters],
        // Query function to make the actual query with.
        queryFn: async ({ queryKey }) => {
            if (DEBUG) console.log("Api :: getChatSession");

            const [_key, parameters] = queryKey;

            const result = await fetchWrapper({
                endpoint: ENDPOINTS.getChatSession,
                parameters,
                msal
            });

            return { queryKey: _key, result };
        },
        // To disable the query, use this.
        enabled: enabled,
        // Make sure this option makes sense to your context.
        refetchOnWindowFocus: false,
    });
}

export function usePostSession({ onSuccess, onError }) {
    //@TEMPLATE_@MSAL - Uncomment this line to use MSAL for authentication.
    const msal = useMsal();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ body }) => {
            if (DEBUG) console.log("Api :: postChatSession");

            const result = await fetchWrapper({
                endpoint: ENDPOINTS.postChatSession,
                body,
                msal
            });

            return { result };
        },
        onError: (error, newData, context) => {
            if (DEBUG) console.log("ProjectChat :: postSessionMutation :: ERROR")
            if (DEBUG) console.log(error);

            if (onError) onError(error);
        },
        onSuccess: (result) => {
            if (DEBUG) console.log("ProjectChat :: postSessionMutation :: onSuccess")
            if (DEBUG) console.log(result);
            queryClient.invalidateQueries([QUERYKEYS.getChatSessions, {}]);

            if (onSuccess) onSuccess(result);
        }
    });
}

export function usePostChatMessage({ onSuccess, onError }) {
    // @TEMPLATE_@MSAL - Uncomment this line to use MSAL for authentication.
    const msal = useMsal();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (body) => {
            if (DEBUG) console.log("Api :: postChatMessage");
            if (DEBUG) console.log(body);

            const result = await fetchWrapper({
                endpoint: ENDPOINTS.postChatMessage,
                body,
                msal
            });

            return { result };
        },
        // Optimistic update
        onMutate: async (parameters) => {
            if (DEBUG) console.log("ProjectChat :: postMessageMutation :: onMutate");
            if (DEBUG) console.log(parameters);

            const {
                searchId,
                sessionId,
                message_content
            } = parameters;
            const searchQueryKey = [
                createContentEndpointUrl({ endpoint: CONTENT_ENDPOINTS.getSearches, parameters: { id: searchId } }), {
                    id: searchId
                }];

            // Cancel pending queries to prevent sync issues
            await queryClient.cancelQueries(searchQueryKey);

            if (DEBUG) console.log("searchQueryKey", searchQueryKey);

            let previousData = queryClient
                .getQueryData(searchQueryKey);

            if (DEBUG) console.log("ProjectChat :: postMessageMutation :: onMutate :: previousData")
            if (DEBUG) console.log(previousData);

            if (!previousData) {
                previousData = {
                    id: searchId,
                    result: {
                        chat_session: {
                            id: sessionId,
                            chat_messages: []
                        }
                    }
                }
            };

            let updatedSearch = {
                ...previousData.result,
                chat_session: {
                    ...previousData.result.chat_session,
                    chat_messages: [
                        ...previousData.result.chat_session.chat_messages,
                        {
                            message_content: message_content,
                            role: 'user',
                            created: Date.now()
                        }
                    ]
                }
            };

            if (DEBUG) console.log("ProjectChat :: postMessageMutation :: onMutate :: updatedSearch");
            if (DEBUG) console.log(updatedSearch);

            queryClient.setQueryData(
                searchQueryKey,
                {
                    queryKey: createContentEndpointUrl({
                        endpoint: CONTENT_ENDPOINTS.getSearches,
                        parameters: { id: searchId }
                    }),
                    result: updatedSearch
                }
            );

            return { previousData, message_content };
        },
        // If the mutation fails, roll back to the previous data
        onError: (error, newData, context) => {
            if (DEBUG) console.log("ProjectChat :: postMessageMutation :: ERROR")
            if (DEBUG) console.log(error);
            console.log("context", context.previousData);

            const searchQueryKey = [
                createContentEndpointUrl({ endpoint: CONTENT_ENDPOINTS.getSearches, parameters: { id: context.searchId } }), {
                    id: context.searchId
                }];

            queryClient.setQueryData(searchQueryKey, context.previousData);

            if (onError) onError(error);
        },
        // After the mutation succeeds, invalidate the query to re-fetch the data
        onSettled: (result, variables, context) => {
            if (DEBUG) console.log("ProjectChat :: postMessageMutation :: onSettled")
            if (DEBUG) console.log(result);
            if (DEBUG) console.log(context);
            const searchQueryKey = [
                createContentEndpointUrl({
                    endpoint: CONTENT_ENDPOINTS.getSearches,
                    parameters: { id: context.searchId }
                }), {
                    id: context.searchId
                }];

            queryClient.invalidateQueries(searchQueryKey);
        },
        onSuccess: (result, context) => {
            if (DEBUG) console.log("ProjectChat :: postMessageMutation :: onSuccess")
            if (DEBUG) console.log(result);
            if (DEBUG) console.log(context);

            const searchQueryKey = [
                createContentEndpointUrl({
                    endpoint: CONTENT_ENDPOINTS.getSearches,
                    parameters: { id: context.searchId }
                }), {
                    id: context.searchId
                }];

            let previousData = queryClient.getQueryData(searchQueryKey);

            if (DEBUG) console.log("ProjectChat :: postMessageMutation :: onSuccess :: previousData");
            if (DEBUG) console.log(searchQueryKey);
            if (DEBUG) console.log(previousData);

            if (!previousData) {
                // TODO: BETTER ERROR HANDLING
                return;
            };

            let updatedSession = {
                ...previousData.result,
                chat_session: {
                    ...previousData.result.chat_session,
                    chat_messages: [
                        ...previousData.result.chat_session.chat_messages,
                        result.result
                    ]
                },
            };

            if (DEBUG) console.log("ProjectChat :: postMessageMutation :: onSuccess :: updatedSession");
            if (DEBUG) console.log(updatedSession);

            queryClient.setQueryData(searchQueryKey, {
                queryKey: createContentEndpointUrl({
                    endpoint: CONTENT_ENDPOINTS.getSearches,
                    parameters: { id: context.searchId }
                }),
                result: updatedSession
            });

            if (onSuccess) onSuccess(result);
        }
    });
}